import { useCallback, useEffect, useReducer, useState } from "react";
import Form from "@src/components/forms/Form";
import { Checkbox, DropDown, FormInput, Loader } from "@src/components/atoms";
import Button from "@src/components/atoms/Button";
import { adminCustomerSchema } from "../formHelpers";
import { useFieldArray } from "react-hook-form";
import AdminHooks from "@src/context/hooks/adminHooks";
import { normalizeEmptyStringsToNull } from "./AdminHelpers";
import moment from "moment";
import { EnrollmentType } from "@signolteam/shared/types/enums";

const languages = [
  { value: "en", text: "english" },
  // { value: "sp", text: "spanish" },
  // { value: "ru", text: "russian" },
];

const industries = [
  { value: "aviation", text: "aviation" },
  { value: "shipping", text: "shipping" },
];

const targetPeriodDurationOptions = [
  { value: "BIWEEKLY", text: "Biweekly" },
  { value: "MONTHLY", text: "Monthly" },
];
const baselinePeriodOptions = [
  { value: 3, text: 3 },
  { value: 4, text: 4 },
  { value: 5, text: 5 },
  { value: 6, text: 6 },
];

export const CustomerForm = ({
  isNew = false,
  data = {},
  onSubmit,
  onCancel,
}) => {
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");

  const initialCustomer = {
    customerCode: data?.customerCode || "",
    name: data?.name || "",
    language: data?.language || "en",
    industry: data?.industry || "",
    targetPeriodDuration: data?.targetPeriodDuration || "",
    baselinePeriod: data?.baselinePeriod || "",
    reportingDelay: data?.reportingDelay || 0,
    preferredEventName: data?.preferredEventName || "",
    preferredEventNamePlural: data?.preferredEventNamePlural || "",
    operatorCodeLabel: data?.operatorCodeLabel || "",
    contractStartDate: data?.contractStartDate || "",
    contractEndDate: data?.contractEndDate || "",
    corporateEmails: data?.corporateEmails || "",
    enrollmentType: data?.enrollmentType || EnrollmentType.AUTO,
  };

  const mutationCallback = (status) => {
    onSubmit && onSubmit();
  };

  const newMutation = AdminHooks.useCreateCustomer(mutationCallback);
  const updateMutation = AdminHooks.useUpdateCustomer(mutationCallback);
  const handleSubmit = async (formData) => {
    setSubmitting(true);
    setError(null);

    const cleaned = normalizeEmptyStringsToNull(formData);
    // this is necessary because only the fields used in the form stay on formData
    if (!isNew) cleaned.id = data.id;
    try {
      !!isNew
        ? await newMutation.mutateAsync(cleaned)
        : await updateMutation.mutateAsync(cleaned);
    } catch (err) {
      setError("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isNew && !data?.name) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col overflow-scroll py-4">
      <div className="mb-4 py-2 border-b-2 border-black text-center font-bold">
        New customer details
      </div>
      <Form
        onSubmit={(data) => {
          handleSubmit(data);
        }}
        schema={adminCustomerSchema}
        options={{ defaultValues: initialCustomer }}
        className="space-y-6"
      >
        {(methods) => {
          const hasChanged = methods.formState.isDirty;
          return (
            <>
              <FormInput
                label="Customer code"
                name="customerCode"
                type="text"
                required
                onChange={(e) => {
                  methods.setValue("customerCode", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                error={methods.formState.errors.customerCode?.message}
              />
              <FormInput
                label="Name"
                name="name"
                type="text"
                required
                onChange={(e) => {
                  methods.setValue("name", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                error={methods.formState.errors.name?.message}
              />
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Language
              </label>
              <DropDown
                label="Language"
                options={languages}
                value={methods.watch("language")}
                onChange={(e) => {
                  methods.setValue("language", e, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Industry
              </label>
              <DropDown
                label="Industry"
                options={industries}
                value={methods.watch("industry")}
                onChange={(e) => {
                  methods.setValue("industry", e, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Target Period Duration
              </label>
              <DropDown
                label="Target Period Duration"
                options={targetPeriodDurationOptions}
                value={methods.watch("targetPeriodDuration")}
                onChange={(e) => {
                  methods.setValue("targetPeriodDuration", e, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Baseline Period (months) -deprecated-
              </label>
              <DropDown
                label="Baseline Period"
                options={baselinePeriodOptions}
                value={methods.watch("baselinePeriod")}
                onChange={(e) => {
                  methods.setValue("baselinePeriod", e, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
              <FormInput
                label="Reporting Delay"
                type="number"
                name="reportingDelay"
                value={methods.watch("reportingDelay")}
                onChange={(e) => {
                  methods.setValue("reportingDelay", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />

              <FormInput
                label="Preferred Event Name"
                name="preferredEventName"
                required
                onChange={(e) => {
                  methods.setValue("preferredEventName", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />

              <FormInput
                label="Preferred Event Name (Plural)"
                name="preferredEventNamePlural"
                required
                onChange={(e) => {
                  methods.setValue("preferredEventNamePlural", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />

              <FormInput
                label="Contract start date"
                name="contractStartDate"
                type="date"
                maxDate={maxDate}
                fValue={moment
                  .utc(methods.watch("contractStartDate"))
                  .format("YYYY-MM-DD")}
                onChange={(e) => {
                  setMinDate(e.target.value);
                  methods.setValue("contractStartDate", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />

              <FormInput
                label="Contract end date"
                name="contractEndDate"
                type="date"
                minDate={minDate}
                fValue={moment
                  .utc(methods.watch("contractEndDate"))
                  .format("YYYY-MM-DD")}
                onChange={(e) => {
                  setMaxDate(e.target.value);
                  methods.setValue("contractEndDate", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />

              <FormInput
                label="Operator Code Label"
                name="operatorCodeLabel"
                onChange={(e) => {
                  methods.setValue("operatorCodeLabel", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />

              <label className="block text-sm font-bold text-gray-700 mb-2">
                Enrollment Type
              </label>
              <DropDown
                label="Enrollment Type"
                options={Object.keys(EnrollmentType).map((key) => ({
                  value: EnrollmentType[key],
                  text: key,
                }))}
                value={methods.watch("enrollmentType")}
                onChange={(e) => {
                  methods.setValue("enrollmentType", e, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
              <FormInput
                label="Corporate Emails -deprecated-"
                name="corporateEmails"
                type="textarea"
                onChange={(e) => {
                  methods.setValue("corporateEmails", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                error={methods.formState.errors.corporateEmails?.message}
              />

              <Button submit loading={submitting}>
                Save changes
              </Button>
              {error && (
                <div className="mt-4 text-xs italic text-red-500">{error}</div>
              )}
            </>
          );
        }}
      </Form>
    </div>
  );
};
